/**
 * Two quiet jobs for the Interact button: cutting a way through barbed wire with wire cutters
 * (04 §6: silent, four seconds, no wound - against `hazards/wire.ts`, which is none of those),
 * and the Salvager perk's one magazine or grenade off a fallen soldier (04 §4).
 */

import type { Ctx } from '../ctx';
import { propDistance } from '../objectives/common';
import { GEAR, PERKS } from '../tuning';
import type { Enemy, PropDef } from '../types';

/** The nearest standing wire run whose footprint is within the cutters' reach, with that distance. */
export function wireInReach(ctx: Pick<Ctx, 'world' | 'statics'>): { prop: PropDef; distance: number } | null {
  const { world: w, statics } = ctx;
  const r = w.ranger;
  let best: { prop: PropDef; distance: number } | null = null;
  for (const prop of statics.def.props) {
    if (prop.kind !== 'wire') continue;
    // Cheap reject before the footprint test: a run is `length` long and he must be beside it.
    const span = (prop.length ?? 4) / 2 + GEAR.cutReach + 1;
    if (Math.abs(prop.x - r.x) > span || Math.abs(prop.z - r.z) > span) continue;
    if (w.props.find((p) => p.id === prop.id)?.destroyed !== false) continue;
    const distance = propDistance(prop, r.x, r.z);
    if (distance <= GEAR.cutReach && (!best || distance < best.distance)) best = { prop, distance };
  }
  return best;
}

/** What this body has on it. A rocket trooper's `grenades` is the rocket in his tube, which is no use to anyone. */
export function salvageYield(e: Enemy): 'magazine' | 'grenade' {
  return e.kind !== 'rocketTrooper' && e.grenades > 0 ? 'grenade' : 'magazine';
}

/** Once per body. */
export function salvage(ctx: Ctx, e: Enemy): void {
  if (e.salvaged) return;
  const inv = ctx.world.ranger.inventory;
  e.salvaged = true;
  if (salvageYield(e) === 'grenade') {
    e.grenades = 0;
    inv.grenades += PERKS.salvageGrenades;
    ctx.events.push({ type: 'pickup', what: '+1 GRENADE' });
  } else {
    inv.magazines += PERKS.salvageMagazines;
    ctx.events.push({ type: 'pickup', what: '+1 MAGAZINE' });
  }
}
